import { sessionApi } from './session';
import { usersApi } from './users';
import type { User } from '../types';

/**
 * Link público de localização de uma máquina.
 *
 * Mecanismo (espelho do app mobile):
 *   1. Pega o usuário logado via GET /api/session
 *   2. Gera UUID v4 e seta em user.token
 *   3. PUT /api/users/{id} com o token novo (servidor passa a aceitar este
 *      token como autenticação alternativa ao cookie)
 *   4. Monta URL `<servidor>/shared-fismatek/?token=...&d=<deviceId>&c=<canControl>&icon=<icon>`
 *
 * IMPORTANTE: o token é por usuário, não por device. Gerar novo link
 * INVALIDA links anteriores compartilhados, e qualquer pessoa com o token
 * vê tudo que esse usuário vê.
 */

function generateToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // fallback simples (improvável de cair aqui em navegador moderno)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

interface ShareOptions {
  deviceId: number;
  /** sufixo usado no marker PNG hospedado pelo servidor: 'online'|'offline'|'static' */
  icon?: 'online' | 'offline' | 'static';
}

export interface ShareResult {
  url: string;
  token: string;
  expiresAt: Date;
}

export const shareApi = {
  /** Gera o link de compartilhamento público de uma máquina. */
  async createShareLink({ deviceId, icon = 'online' }: ShareOptions): Promise<ShareResult> {
    const user = await sessionApi.current();
    if (!user?.id) throw new Error('Sessão expirada. Faça login novamente.');

    const token = generateToken();
    const canControl = user.administrator || (user.userLimit ?? 0) > 0 ? '1' : '0';

    // Atualiza o usuário com o novo token. Mantém todos os campos atuais
    // pra não zerar configurações.
    const updated: User = { ...user, token };
    await usersApi.update(user.id, updated);

    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      token,
      d: String(deviceId),
      c: canControl,
      icon,
    });
    const url = `${baseUrl}/shared-fismatek/?${params.toString()}`;

    // Convenção do app mobile: 365 dias
    const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    return { url, token, expiresAt };
  },
};

